import { Check, Copy } from 'lucide-react';
import type { Code, VoteKind } from '../../shared/types';
import { timeAgo } from '../lib/format';
import { RewardPill } from './RewardPill';

interface Props {
  code: Code;
  now: number;
  copied: boolean;
  myVote: VoteKind | undefined;
  onCopy(value: string): void;
  onVote(codeId: string, vote: VoteKind): void;
}

export function CodeCard({ code, now, copied, myVote, onCopy, onVote }: Props) {
  return (
    <li className="card" data-verdict={code.verdict}>
      <span className="seam" data-verdict={code.verdict} aria-hidden="true" />

      <button
        type="button"
        className={`card-code${copied ? ' sweep' : ''}`}
        onClick={() => onCopy(code.code)}
        aria-label={`Copy ${code.code}`}
      >
        {code.code}
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>

      <div className="card-rewards">
        {code.rewards.map((reward) => (
          <RewardPill reward={reward} key={reward.item} />
        ))}
      </div>

      {note(code) && <p className="card-note">{note(code)}</p>}

      <div className="card-foot">
        <span>{status(code, now)}</span>

        {code.status === 'active' && (
          <div className="vote">
            <button
              type="button"
              className="button"
              aria-label={`Report that ${code.code} worked`}
              disabled={myVote !== undefined}
              onClick={() => onVote(code.id, 'works')}
            >
              {myVote === 'works' ? 'Worked ✓' : 'Worked'}
            </button>
            <button
              type="button"
              className="button"
              aria-label={`Report that ${code.code} did not work`}
              disabled={myVote !== undefined}
              onClick={() => onVote(code.id, 'fails')}
            >
              {myVote === 'fails' ? "Didn't ✓" : "Didn't"}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

/** Expired codes explain themselves; live ones flag disputed rewards. */
function note(code: Code): string | null {
  return code.status === 'expired' ? code.expiryReason : code.rewardNote;
}

function status(code: Code, now: number): string {
  if (code.status === 'expired') return 'Expired';

  const { works, fails, lastWorkedAt } = code.votes;
  const total = works + fails;
  if (total === 0) return 'No reports yet';

  const when = lastWorkedAt ? ` · ${timeAgo(lastWorkedAt, now)}` : '';
  return `${works} of ${total} worked${when}`;
}
